import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ApiService } from '@app/services/api.service';
import { AppState } from '@app/types/app.types';
import {
  Filter,
  FilterGroup,
  FilterMETA,
  FilterOption,
  FilterType,
  SELECTED_FILTER_VALUES,
} from '@app/types/filter.types';

import { IonLabel, IonToggle, ModalController, ToastController } from '@ionic/angular/standalone';
import { logOutOutline } from 'ionicons/icons';

import {
  IonContent,
  IonSpinner,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonIcon,
  IonButton,
  IonButtons,
  IonSelectOption,
  IonSelect,
  IonCol,
  IonGrid,
  IonRow,
  IonCardContent,
  IonCard,
  IonCardTitle,
  IonCardHeader,
} from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { Observable, timeout } from 'rxjs';
import { ToasterService } from '@app/services/toaster.service';
import { setHardFilters } from '@app/state/actions/app.action';
import { jsonCopy } from '@app/utils/app.utils';
import { addIcons } from 'ionicons';
import { ToastMessages } from '@app/constants/toast-messages.constants';
import { NbsSelectDropdownComponent } from '../nbs-select-dropdown/nbs-select-dropdown.component';

@Component({
  selector: 'app-setup-preference',
  templateUrl: './setup-preference.component.html',
  styleUrls: ['./setup-preference.component.css'],
  standalone: true,
  imports: [
    IonContent,
    IonSpinner,
    IonHeader,
    IonTitle,
    IonToolbar,
    IonIcon,
    IonButton,
    IonButtons,
    IonSelect,
    IonSelectOption,
    IonCol,
    IonGrid,
    IonRow,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    CommonModule,
    IonToggle,
    IonLabel,
    NbsSelectDropdownComponent,
  ],
})
export class SetupPreferenceComponent implements OnInit {
  filters$: Observable<Filter[] | null>;
  hardFilters$: Observable<SELECTED_FILTER_VALUES[] | null> | undefined;
  toggleState: string[] = ['CR'];

  @Input() title: string = 'Edit User Preferences for Recs';
  // @Input() buttonText: string = 'Save Preference';
  loading: boolean = false;
  // @Input() loading: boolean = false;

  // Outputs to communicate with the parent (SettingsComponent)
  @Output() preferenceChange = new EventEmitter<SELECTED_FILTER_VALUES[]>();
  @Output() hasUnsavedChanges = new EventEmitter<boolean>();
  @Output() preferenceSaved = new EventEmitter<void>(); // New: Emits on successful save
  @Output() saveError = new EventEmitter<any>(); // New: Emits on save error

  private customerFilters: any[] = [];
  private materialFilters: any[] = [];

  copyOfHardFilter: SELECTED_FILTER_VALUES[] = [];
  listOfSelectedFilterMetaData: FilterMETA[] = [];
  selectedFilters: SELECTED_FILTER_VALUES[] = [];
  private tempFilterData: SELECTED_FILTER_VALUES[] = [];
  filterList: any[] = [];
  customerFiltersForDisplay: any[] = [];
  materialFiltersForDisplay: any[] = [];
  customerFiltersBackUp: any[] = [];
  materialFiltersBackUp: any[] = [];
  voidFiltersBackUp: any[] = [];
  isFormSubmitted: boolean = false;
  errorData: any;
  isFormValid: boolean = true;

  customerFilterOrderMap: any = {
    highLevelTc: 0,
    tradeChannel: 1,
    subTradeChannel: 2,
    tradeName: 3,
    primaryGroup: 4,
    salesOffice: 5,
    marketUnit: 6,
    preferredOrderingMethod: 7,
  };

  materialFilterOrderMap: any = {
    category: 0,
    brand: 1,
    package: 2,
    sku: 3,
    consumptionType: 4,
  };

  isEqualArray(arr1: any[] | undefined, arr2: any[]): boolean {
    if (arr1?.length !== arr2?.length) return false;
    return arr1?.every((val, index) => val === arr2[index]);
  }

  constructor(
    private apiService: ApiService,
    private modalController: ModalController,
    private store: Store<{ app: AppState }>,
    private toastService: ToasterService,
  ) {
    addIcons({ logOutOutline });
    this.filters$ = this.store.select((state) => {
      return state.app.prefFilters;
    });

    this.hardFilters$ = this.store.select((state) => {
      return state.app.hardFilters;
    });
  }

  ngOnInit(): void {
    this.filters$.subscribe((filters) => {
      if (Array.isArray(filters)) {
        this.filterList = filters;
        const customerFilters: any[] = [];
        const materialFilters: any[] = [];

        for (const filter of filters) {
          if (filter.filter_type === FilterType.CUSTOMER) {
            customerFilters.push({ ...filter, order: this.customerFilterOrderMap[filter.filter_key] });
          } else if (filter.filter_type === FilterType.MATERIAL) {
            materialFilters.push({ ...filter, order: this.materialFilterOrderMap[filter.filter_key] });
          }
        }

        this.materialFilters = materialFilters.sort((a, b) => a.order - b.order);
        this.customerFilters = customerFilters.sort((a, b) => a.order - b.order);

        this.customerFiltersForDisplay = jsonCopy(this.customerFilters);
        this.materialFiltersForDisplay = jsonCopy(this.materialFilters);
      }
    });

    this.hardFilters$?.subscribe((hardFilters: any) => {
      if (Array.isArray(hardFilters)) {
        this.copyOfHardFilter = hardFilters;
        for (const filter of hardFilters) {
          const selectedFilter = this.filterList.find((item) => item.filter_key === filter.filterKey);
          // Trigger initial check for unsaved changes
          this.emitUnsavedChangesStatus();
          // Emit initial preference state
          this.preferenceChange.emit(this.selectedFilters);
          this.onFilterChange(filter.filterKey, filter.values, selectedFilter);
          // console.log('this.', this.selectedFilters);
        }
      }
    });
  }

  // This method now determines if there are unsaved changes
  // and emits the status to the parent.
  emitUnsavedChangesStatus(): void {
    const hasChanges = !this.areFiltersEqual(this.selectedFilters, this.copyOfHardFilter);
    this.hasUnsavedChanges.emit(hasChanges);
  }

  // Helper to compare two arrays of SELECTED_FILTER_VALUES
  private areFiltersEqual(arr1: SELECTED_FILTER_VALUES[], arr2: SELECTED_FILTER_VALUES[]): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }

    // Create maps for easier comparison
    const map1 = new Map<string, any[]>();
    arr1.forEach((f) => map1.set(f.filterKey, [...f.values].sort())); // Sort values for consistent comparison

    const map2 = new Map<string, any[]>();
    arr2.forEach((f) => map2.set(f.filterKey, [...f.values].sort()));

    if (map1.size !== map2.size) {
      return false;
    }

    for (const [key, values1] of map1.entries()) {
      const values2 = map2.get(key);
      if (!values2 || !this.isEqualArray(values1, values2)) {
        return false;
      }
    }
    return true;
  }

  storeAdvacneFilters(data: any) {
    const filters: Filter[] = data.map((filter: any) => {
      const copyOfFilter = { ...filter };
      copyOfFilter.default_options = copyOfFilter.default_options
        .map((option: any) => {
          if (Array.isArray(option.metadata) && option.metadata[0]) {
            const output = Object.entries(option.metadata[0]).reduce((acc, [key, values]) => {
              (values as string[]).forEach((value) => acc.push(`${key}_${value}`));
              return acc;
            }, [] as string[]);
            option.metadata = output;
          }

          return option;
        })
        .sort((a: any, b: any) => {
          return a.label.localeCompare(b.label);
        });

      return copyOfFilter;
    });
  }

  postSavePreferenceData(obj: any) {
    this.apiService.PostUserPreference('users/post-user-preference', obj).subscribe(
      async (response) => {
        // Success: Stop loading, show success toast, and dismiss modal
        this.loading = false;
        this.toastService.success({ message: ToastMessages.preferences.success });
        this.modalController.dismiss();
        const hardFilters: SELECTED_FILTER_VALUES[] = response.data?.preferences.hardFilters.map((preference: any) => {
          let filterKey = preference.filterKey;
          return { filterKey, values: preference.values };
        });
        this.store.dispatch(setHardFilters({ hardFilters }));
        this.preferenceSaved.emit();
      },
      async (error) => {
        // Error: Stop loading, show error toast, and keep modal open
        setTimeout(() => {
          this.loading = false;
        }, 3000);
        this.toastService.error({ message: ToastMessages.preferences.error, payload: { message: error.message } });
        console.log(error);
        this.saveError.emit();
      },
    );
  }

  applyFilters() {
    this.loading = true;
    const payload = {
      hardFilters: this.selectedFilters.map((filter) => {
        return { filterKey: filter.filterKey, values: filter.values };
      }),
      selectedRole: this.selectedFilters
        .filter((filter) => filter.filterKey === 'highLevelTc')
        .map((filter) => filter.values[0])[0],
    };
    this.postSavePreferenceData(payload);
  }

  getFilterValues(filterKey: string) {
    let index;
    index = this.selectedFilters.findIndex((item) => item.filterKey === filterKey);
    if (index !== -1) {
      return this.selectedFilters[index].values;
    }

    return [];
  }

  findFilterOptionByValue(filterKey: any, filterOptionValue: any): SELECTED_FILTER_VALUES[] {
    const foundFilter = this.filterList.find((item) => item.filter_key === filterKey);

    if (!foundFilter) return [];

    const foundFilterOption = foundFilter.default_options.filter((item: any) => filterOptionValue.includes(item.value));

    return foundFilterOption;
  }

  onFilterChange(filterKey: string, selectedValues: any, filter: any) {
    try {
      let prevFirstFilterKey = this.selectedFilters?.find((item) => item.filterType === filter.filter_type)?.filterKey;

      const selectedFilterIndex = this.selectedFilters?.findIndex((filter) => {
        return filter.filterKey === filterKey;
      });
      const selectionOptions: SELECTED_FILTER_VALUES[] = this.findFilterOptionByValue(filterKey, selectedValues);
      const listOfFilterKeysByFilterType = jsonCopy(
        filter.filter_type === 'CUSTOMER_FILTER' ? this.customerFilters : this.materialFilters,
      ).map((item: Filter) => item.filter_key);

      if (selectedFilterIndex !== -1) {
        if (selectedValues.length) {
          if (this.selectedFilters && this.selectedFilters[selectedFilterIndex]) {
            this.selectedFilters[selectedFilterIndex].values = selectedValues;
          }

          if (this.selectedFilters && Array.isArray(this.selectedFilters[selectedFilterIndex].metadata)) {
            const combinedMeta = selectionOptions.reduce((acc: string[], item: SELECTED_FILTER_VALUES) => {
              const listOfMetadata = item.metadata?.filter((item) => {
                const extractedFilterKey = item.split('_')[0];
                return listOfFilterKeysByFilterType.includes(extractedFilterKey);
              });

              return [...acc, ...(listOfMetadata || [])];
            }, []);
            this.selectedFilters[selectedFilterIndex].metadata = [...new Set([...combinedMeta])];
          }
        } else {
          this.selectedFilters?.splice(selectedFilterIndex, 1);
        }
      } else {
        const combinedMeta = selectionOptions.reduce((acc: string[], item: SELECTED_FILTER_VALUES) => {
          return [...acc, ...(item.metadata || [])];
        }, []);
        this.selectedFilters?.push({
          filterKey: filterKey,
          values: selectedValues,
          filterType: filter.filter_type,
          metadata: combinedMeta,
          name: filter.filter_display_name,
        });
      }

      let firstFilterKey = this.selectedFilters?.find((item) => item.filterType === filter.filter_type)?.filterKey;

      if (filter.filter_type != 'VOID_FILTER') {
        if (!prevFirstFilterKey && firstFilterKey) {
          this.rePopulateFilters(firstFilterKey, filter.filter_type as FilterType);
        } else if (firstFilterKey === prevFirstFilterKey) {
          this.rePopulateFilters(firstFilterKey, filter.filter_type as FilterType);
        } else if (!firstFilterKey || prevFirstFilterKey !== firstFilterKey) {
          this.rePopulateFilters(firstFilterKey, filter.filter_type as FilterType);
        }
      }

      // Emit the updated selectedFilters to the parent
      this.preferenceChange.emit(this.selectedFilters);
      // Emit whether there are unsaved changes
      this.emitUnsavedChangesStatus();
    } catch (error) {
      console.log(error);
    }
  }

  rePopulateFilters(firstSelectedFilterKey: string | undefined, filterType: FilterType) {
    const copyOfFilters = jsonCopy(filterType === 'CUSTOMER_FILTER' ? this.customerFilters : this.materialFilters);

    for (const filerIndex in copyOfFilters) {
      const filter = copyOfFilters[filerIndex];

      if (firstSelectedFilterKey && filter.filter_key !== firstSelectedFilterKey) {
        let indexOfCurrentFilter = (this.selectedFilters || []).findIndex(
          (item) => item.filterKey === filter.filter_key,
        );

        if (indexOfCurrentFilter === -1) {
          indexOfCurrentFilter = this.selectedFilters.length ? this.selectedFilters.length - 1 : 0;
        }

        const combinedArray = jsonCopy(this.selectedFilters)
          .slice(0, indexOfCurrentFilter + 1)
          .filter((item: SELECTED_FILTER_VALUES) => item.filterType === filterType)
          .flatMap((item: any) => item.metadata);

        const filteredOptions = filter.default_options.filter((option: FilterOption) => {
          const result = combinedArray?.includes(`${filter.filter_key}_${option.value}`);

          if (!result) {
            this.unSetOutOfBoundValue(filter.filter_key, option, filterType);
          }

          return result;
        });

        this.updateFilterOptions(filterType as FilterType, filerIndex as unknown as number, filteredOptions);
      } else {
        this.updateFilterOptions(filterType as FilterType, filerIndex as unknown as number, filter.default_options);
      }
    }
  }

  unSetOutOfBoundValue(filterKey: string, option: FilterOption, filterType: FilterType) {
    let copyArray = [...this.selectedFilters];

    const foundIndexOfFilter = copyArray.findIndex((item) => item.filterKey === filterKey);
    if (foundIndexOfFilter > -1) {
      const filter = this.selectedFilters[foundIndexOfFilter];
      filter.values = filter.values.filter((item: string | number) => {
        const localizedString = typeof item === 'number' || typeof item === 'string' ? item.toLocaleString() : item;
        return localizedString !== option.value;
      });
      if (filter.values.length === 0) {
        copyArray.splice(foundIndexOfFilter, 1);
      }
    }

    this.selectedFilters = copyArray;
    const someFilterOfFilterTypeSelected = copyArray.some((item) => item.filterType === filterType);
    if (!someFilterOfFilterTypeSelected) {
      if (filterType === FilterType.MATERIAL) {
        this.materialFilters = jsonCopy(this.materialFilters);
      } else {
        this.customerFilters = jsonCopy(this.customerFilters);
      }
    }
  }

  updateFilterOptions(filterType: FilterType, filterIndex: number, options: SELECTED_FILTER_VALUES[]) {
    if (filterType === 'CUSTOMER_FILTER') {
      this.customerFiltersForDisplay[filterIndex as any].default_options = options;
    }
    if (filterType === 'MATERIAL_FILTER') {
      this.materialFiltersForDisplay[filterIndex as any].default_options = options;
    }
  }

  getIsMultiSelect(data: any) {
    return data.filter_input_type === 'MULTI_SELECT';
  }

  cancelButton() {
    this.modalController.dismiss();
  }

  isButtonDisabled(): boolean {
    if (Array.isArray(this.copyOfHardFilter) && this.copyOfHardFilter.length > 0) {
      return !!this.filterList?.every((selectedFilter) => {
        const previousFilter = this.copyOfHardFilter?.find((item) => item.filterKey === selectedFilter.filter_key);
        const filter = this.selectedFilters?.find((item) => item.filterKey === selectedFilter.filter_key);
        return filter?.values.join() === previousFilter?.values.join();
      });
    }
    return !(this.selectedFilters.length > 0);
  }
}
